/**
 * Instant "Start recording" for lite mode — the NewMeetingDialog mutation
 * (create meeting → sessionStorage handoff → navigate) without mounting the
 * dialog. `mode: "live"` is required on the config payload; without it the
 * recorder falls back to batch and the live screen is empty.
 *
 * Languages: this device's last recording, then the workspace default.
 * The start waits until that default has loaded, so an empty cache is not
 * treated as auto-detect.
 */

import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import { useCallback, useRef } from "react"
import { useAuth } from "../../hooks/useAuth"
import { useCompanyApi } from "../../hooks/useCompanyApi"
import { useCompanyQuery } from "../../hooks/useCompanyQuery"
import { useNetworkStatus } from "../../hooks/useNetworkStatus"
import { fetchSettings, startBrowserMeeting } from "../../lib/api"
import { readLastUsedLanguages, rememberLastUsedLanguages } from "../../lib/last-used-languages"
import { languagesFromDefaultSetting } from "../../lib/meeting-start-config"
import { notifyError } from "../../lib/notify"
import { queryKeys } from "../../lib/query-keys"
import type { IDialogStartConfig } from "./interfaces"

const LITE_TEMPLATE_ID = "meeting-lite"

export interface IInstantStart {
  start: () => Promise<void>
  isStarting: boolean
  /**
   * False while the workspace language is still loading and this device has
   * no remembered languages. `start` waits rather than guessing; a caller can
   * disable the button with this.
   */
  settingsReady: boolean
}

export { languagesFromDefaultSetting }

function defaultLanguageFromSettings(data: unknown): string | undefined {
  if (!data || typeof data !== "object" || !("default_language" in data)) return undefined
  const value = data.default_language
  return typeof value === "string" ? value : undefined
}

export function useInstantStart(): IInstantStart {
  const company = useCompanyApi()
  const { user } = useAuth()
  const orgId = user?.activeOrganizationId ?? null
  const { i18n } = useLingui()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { isOnline } = useNetworkStatus()

  const settingsQuery = useCompanyQuery({
    key: queryKeys.companySettingsForNewMeeting,
    queryFn: (client) => fetchSettings(client),
    staleTime: 5 * 60_000,
  })

  const companyRef = useRef(company)
  companyRef.current = company
  const onlineRef = useRef(isOnline)
  onlineRef.current = isOnline
  const orgIdRef = useRef(orgId)
  orgIdRef.current = orgId
  const settingsRef = useRef(settingsQuery)
  settingsRef.current = settingsQuery

  const settingsReady =
    settingsQuery.data !== undefined || settingsQuery.isError === true || readLastUsedLanguages() !== null

  const startMutation = useMutation({
    mutationFn: async (): Promise<void> => {
      const last = readLastUsedLanguages()
      let languages: string[]
      let autoDetect: boolean
      if (last) {
        languages = last.languages
        autoDetect = last.autoDetect
      } else {
        const snapshot = settingsRef.current
        let raw: unknown
        if (snapshot.data !== undefined) {
          raw = snapshot.data
        } else if (snapshot.isError === true || !onlineRef.current) {
          throw new Error(i18n._(msg`Workspace language hasn't loaded yet. Try again in a moment.`))
        } else {
          const client = companyRef.current
          if (!client) {
            throw new Error(i18n._(msg`No active workspace. Please select an organisation and try again.`))
          }
          raw = await queryClient.fetchQuery({
            queryKey: queryKeys.companySettingsForNewMeeting(orgIdRef.current),
            staleTime: 5 * 60_000,
            queryFn: () => fetchSettings(client),
          })
        }
        const resolved = languagesFromDefaultSetting(defaultLanguageFromSettings(raw))
        languages = resolved.languages
        autoDetect = resolved.autoDetect
      }

      const config: IDialogStartConfig = { languages, autoDetect, mode: "live" }
      rememberLastUsedLanguages(config)

      if (!onlineRef.current) {
        const provisionalId = `provisional_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`
        sessionStorage.setItem(
          `provisional:${provisionalId}`,
          JSON.stringify({ subject: "", templateId: LITE_TEMPLATE_ID }),
        )
        sessionStorage.setItem(`meeting-config:${provisionalId}`, JSON.stringify(config))
        navigate({ to: "/meetings/$id", params: { id: provisionalId } })
        return
      }

      const activeCompany = companyRef.current
      if (!activeCompany) {
        throw new Error(i18n._(msg`No active workspace. Please select an organisation and try again.`))
      }

      const created = await startBrowserMeeting(activeCompany, "", LITE_TEMPLATE_ID)
      const id =
        created && typeof created === "object" && "id" in created && typeof created.id === "string" ? created.id : ""
      if (!id) {
        throw new Error(i18n._(msg`Could not start the recording. Try again.`))
      }
      sessionStorage.setItem(`meeting-config:${id}`, JSON.stringify(config))
      navigate({ to: "/meetings/$id", params: { id } })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.meetings(orgId) })
    },
  })

  const start = useCallback(async (): Promise<void> => {
    try {
      await startMutation.mutateAsync()
    } catch (err) {
      notifyError(err)
    }
  }, [startMutation])

  return { start, isStarting: startMutation.isPending, settingsReady }
}
